import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import type { CampaignConfigIdentityRow } from '@/lib/campaign/campaign-config';
import { supabase } from '@/lib/supabase';

export type CampaignConfigRow = CampaignConfigIdentityRow;

// userId is deliberately absent: unlike os_campaign_stats/os_campaign_funnel,
// which are security_invoker views over seat-scoped os_linkedin_drafts,
// os_campaign_config is a TABLE whose member-facing READ policy is
// campaign_config_member_read — FOR SELECT, USING user_belongs_to_org(organization_id),
// i.e. membership-wide with no role test (migration 20260810140000). Two seats in
// one organization therefore see byte-identical rows, which is what makes a
// user-scoped cache key wrong rather than merely redundant.
//
// Writes are NOT membership-wide: INSERT and UPDATE are owner/admin-only in the
// same migration. Do not read this comment as evidence that member writes are
// intentional — the read is deliberately open so the security_invoker views that
// LEFT JOIN this table keep resolving real campaign names for every member.
export const campaignConfigQueryKey = (
  organizationId: string | null,
  campaignId: string | undefined,
): readonly ['campaign-config', string | null, string | undefined] =>
  ['campaign-config', organizationId, campaignId];

export const useCampaignConfig = (
  campaignId: string | undefined,
): UseQueryResult<CampaignConfigRow | null> => {
  const { organizationId } = useAuth();

  return useQuery<CampaignConfigRow | null>({
    queryKey: campaignConfigQueryKey(organizationId, campaignId),
    queryFn: async () => {
      if (!campaignId || !organizationId) return null;

      const { data, error } = await supabase
        .from('os_campaign_config')
        .select('campaign_name, product_line')
        .eq('organization_id', organizationId)
        .eq('heyreach_campaign_id', campaignId)
        .maybeSingle();

      if (error) throw error;
      return (data as CampaignConfigRow | null) ?? null;
    },
    enabled: !!campaignId && !!organizationId,
    staleTime: 30_000,
  });
};
